import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Nav } from '@zendeskgarden/react-chrome';
import { useNavigate } from 'react-router-dom';
import { SignOutIcon } from './Icons';
import { useAuth } from '../../utils/auth';

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  isActive?: boolean;
}

interface SidebarProps {
  items: SidebarItem[];
  activeItem: string;
  onItemClick: (itemId: string) => void;
  onLogout: () => void;
  userInfo?: string;
  isExpanded?: boolean;
  onToggle?: () => void;
}

const SidebarContainer = styled.div<{ $isExpanded: boolean }>`
  position: relative;
  z-index: 1000;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 9999; /* Highest z-index on mobile */
    transform: ${props => props.$isExpanded ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease;
    box-shadow: ${props => props.$isExpanded ? '2px 0 8px rgba(0,0,0,0.1)' : 'none'};
  }
`;

const Overlay = styled.div<{ $isVisible: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isVisible ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9998; /* Just below sidebar */
  }
`;

const StyledNav = styled(Nav)<{ $isExpanded: boolean }>`
  background-color: white !important;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  border-right: 1px solid #e9ebed;
  margin-left: 16px;
  margin-top: 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #e9ebed;
  width: 250px;
  height: calc(100vh - 32px);
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f3f4;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #dadce0;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #bdc1c6;
  }
  
  @media (max-width: 768px) {
    margin: 0;
    width: 280px;
    height: 100vh;
    border-radius: 0;
    border-right: none;
    border: none;
    box-shadow: 2px 0 8px rgba(0,0,0,0.1);
    
    /* Enhanced scrollbar for mobile */
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f8f9fa;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #8D4F1B;
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: #703815;
    }
  }
  
  @media (max-width: 480px) {
    width: 260px;
  }
`;

const LogoSection = styled.div`
  padding: 24px 20px;
  border-bottom: 1px solid #e9ebed;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 20px 16px;
    border-bottom: 1px solid #e9ebed;
  }
  
  @media (max-width: 480px) {
    padding: 16px 12px;
  }
`;

const LogoImage = styled.img`
  width: 200px;
  
  @media (max-width: 768px) {
    width: 180px;
  }
  
  @media (max-width: 480px) {
    width: 160px;
  }
`;

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  
  @media (max-width: 768px) {
    padding: 12px 0;
  }
  
  @media (max-width: 480px) {
    padding: 8px 0;
  }
`;

const NavItem = styled.li`
  margin-bottom: 4px;
  
  @media (max-width: 768px) {
    margin-bottom: 2px;
  }
`;

const NavButton = styled.button<{ $isActive: boolean }>`
  width: 85%;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: none;
  background: ${props => props.$isActive ? 'rgba(141, 79, 27, 0.1)' : 'transparent'};
  color: ${props => props.$isActive ? '#8D4F1B' : '#68737d'};
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  border-radius: 8px;
  margin: 0 16px;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background-color: ${props => props.$isActive ? 'rgba(141, 79, 27, 0.1)' : '#f8f9fa'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(141, 79, 27, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 90%;
    padding: 14px 16px;
    margin: 0 12px;
    font-size: 16px;
    
    /* Better touch targets for mobile */
    min-height: 48px;
    
    &:active {
      transform: scale(0.98);
    }
  }
  
  @media (max-width: 480px) {
    width: 92%;
    padding: 12px 12px;
    margin: 0 8px;
    font-size: 15px;
  }
`;

const LogoutSection = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 0 16px 0;
  margin-top: auto;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 0 0 20px 0;
  }
  
  @media (max-width: 480px) {
    padding: 0 0 16px 0;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: none;
  background: transparent;
  color: #CC3340;
  cursor: pointer;
  font-size: 14px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  border-radius: 8px;
  margin: 0 16px;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background-color: rgba(204, 51, 64, 0.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(204, 51, 64, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    margin: 0 12px;
    font-size: 16px;
    min-height: 48px;
    
    &:active {
      transform: scale(0.98);
    }
  }
  
  @media (max-width: 480px) {
    padding: 12px 12px;
    margin: 0 8px;
    font-size: 15px;
  }
`;

const UserSection = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e9ebed;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    margin-bottom: 12px;
  }
`;

const UserInfo = styled.div`
  color: #2f3941;
  font-size: 14px;
  font-weight: 500;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const UserRole = styled.div`
  color: #68737d;
  font-size: 12px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  margin-top: 2px;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const Sidebar: React.FC<SidebarProps> = ({ 
  items, 
  activeItem, 
  onItemClick, 
  onLogout,
  userInfo,
  isExpanded: controlledExpanded,
  onToggle
}) => {
  const { user } = useAuth();
  const [internalExpanded, setInternalExpanded] = useState(window.innerWidth >= 768);
  
  // Use controlled state if provided, otherwise use internal state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        if (controlledExpanded === undefined) {
          setInternalExpanded(true);
        }
      } else {
        if (controlledExpanded === undefined) {
          setInternalExpanded(false);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [controlledExpanded]);

  const toggleSidebar = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const handleItemClick = (itemId: string) => {
    onItemClick(itemId);
    // Close sidebar on mobile after item click
    if (window.innerWidth < 768) {
      if (onToggle) {
        onToggle();
      } else {
        setInternalExpanded(false);
      }
    }
  };

  const handleLogoutClick = () => {
    onLogout();
    // Close sidebar on mobile after logout
    if (window.innerWidth < 768) {
      if (onToggle) {
        onToggle();
      } else {
        setInternalExpanded(false);
      }
    }
  };

  return (
    <>
      <Overlay $isVisible={isExpanded && window.innerWidth < 768} onClick={toggleSidebar} />
      
      <SidebarContainer $isExpanded={isExpanded}>
        <StyledNav $isExpanded={isExpanded}>
          <LogoSection>
            <LogoImage src="/dermAI_logo.svg" alt="DermAI Logo" />
          </LogoSection>
          
          {user && (
            <UserSection>
              <UserInfo>{user.name || 'User'}</UserInfo>
              <UserRole>{user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}</UserRole>
            </UserSection>
          )}
          
          <NavList>
            {items.map((item) => (
              <NavItem key={item.id}>
                <NavButton
                  $isActive={activeItem === item.id}
                  onClick={() => handleItemClick(item.id)}
                  title={item.label}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavButton>
              </NavItem>
            ))}
          </NavList>
          
          <LogoutSection>
            <li>
              <LogoutButton onClick={handleLogoutClick} title="Logout">
                <SignOutIcon />
                <span>Logout</span>
              </LogoutButton>
            </li>
          </LogoutSection>
        </StyledNav>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
